class ShareCartComponent extends HTMLElement {
  #resetTimer;

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const urlInput = event.target.closest('[data-share-cart-url]');
    if (urlInput instanceof HTMLInputElement && this.contains(urlInput)) {
      urlInput.select();
      return;
    }

    const button = event.target.closest('[data-share-cart-copy]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    this.#copyCartUrl(button);
  };

  #handleInput = (event) => {
    const noteInput = event.target;
    if (!(noteInput instanceof HTMLInputElement) || !noteInput.matches('[data-share-cart-note]')) return;
    if (!this.contains(noteInput)) return;

    this.#updateCartUrl(noteInput.value);
  };

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('input', this.#handleInput);
    this.#openFromHash();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('input', this.#handleInput);
    window.clearTimeout(this.#resetTimer);
  }

  #openFromHash() {
    if (window.location.hash !== '#share-cart') return;

    const details = this.querySelector('.share-cart__details');
    const trigger = details?.querySelector('summary');
    if (!(details instanceof HTMLDetailsElement) || !(trigger instanceof HTMLElement)) return;

    details.open = true;
    window.requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
  }

  /** @param {string} note */
  #updateCartUrl(note) {
    const baseCartUrl = this.dataset.cartUrl;
    const urlInput = this.querySelector('[data-share-cart-url]');
    if (!baseCartUrl || !(urlInput instanceof HTMLInputElement)) return;

    try {
      const cartUrl = new URL(baseCartUrl, window.location.origin);
      const trimmedNote = note.trim();

      if (trimmedNote) {
        cartUrl.searchParams.set('note', trimmedNote);
      } else {
        cartUrl.searchParams.delete('note');
      }

      urlInput.value = cartUrl.toString();
    } catch (error) {
      console.warn('[share-cart] Failed to build cart URL:', error);
      urlInput.value = baseCartUrl;
    }
  }

  /** @param {HTMLButtonElement} button */
  async #copyCartUrl(button) {
    const urlInput = this.querySelector('[data-share-cart-url]');
    if (!(urlInput instanceof HTMLInputElement) || !urlInput.value) return;

    button.disabled = true;

    try {
      await this.#writeToClipboard(urlInput.value);
      this.#showStatus(button, this.dataset.copiedMessage || 'Copied!');
    } catch (error) {
      console.warn('[share-cart] Failed to copy cart URL:', error);
      this.#showStatus(button, this.dataset.errorMessage || 'Failed to copy');
    } finally {
      button.disabled = false;
    }
  }

  /** @param {string} text */
  async #writeToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch {
        // Fall through for browsers that expose the Clipboard API but deny access.
      }
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      if (!document.execCommand('copy')) throw new Error('The copy command was rejected');
    } finally {
      textArea.remove();
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} message
   */
  #showStatus(button, message) {
    const label = button.querySelector('[data-share-cart-button-label]');
    const status = this.querySelector('[data-share-cart-status]');
    const defaultLabel = this.dataset.buttonLabel || 'Copy link';

    if (label) label.textContent = message;
    if (status) status.textContent = message;

    window.clearTimeout(this.#resetTimer);
    this.#resetTimer = window.setTimeout(() => {
      if (label) label.textContent = defaultLabel;
      if (status) status.textContent = '';
    }, 1500);
  }
}

if (!customElements.get('share-cart-component')) {
  customElements.define('share-cart-component', ShareCartComponent);
}
